/**
 * Notes API — typed wrappers around note endpoints.
 *
 * Uses generated OpenAPI types for request/response shapes so the compiler
 * catches drift between the FastAPI backend and React frontend.
 *
 * Runtime behaviour is identical to the previous notes.js — only types added.
 */

import type { NoteOut } from '../types/generated/notes-types';
import { typedPost, typedPut, typedFetchRaw, ApiError } from './client';
import { logger } from '../utils/logger';

/* -------------------------------------------------------------------------- */
/*  Re-export domain types so consumers can import from this module           */
/* -------------------------------------------------------------------------- */

export type { NoteOut };

export interface PaperworkProposalItem {
  source_field_path: string;
  source_label: string;
  answer: unknown;
  outcome: 'mapped';
  destination_field_id: string | null;
  match_method: string | null;
  attribution?: Record<string, unknown>;
  already_imported_in_note?: boolean;
}

export interface PaperworkAdditionalItem {
  source_field_path: string;
  source_label: string;
  answer: unknown;
  outcome: 'additional';
  configured_destination_field_id: string | null;
  attribution?: Record<string, unknown>;
  already_imported_in_note?: boolean;
}

export interface PaperworkAvailableTopic {
  topic_id: string;
  label: string;
}

export interface PaperworkConfigurationError {
  source_field_path: string;
  source_label: string;
  configured_destination_field_id: string | null;
  reason: string;
}

export interface PaperworkPriorImport {
  note_id: string;
  note_date: string | null;
  imported_at: string;
}

export interface PaperworkProposal {
  response_id: string;
  revision_id: string | null;
  assignment_id: string;
  form_version_id: string;
  title: string;
  matched_count: number;
  additional_count: number;
  configuration_error_count?: number;
  configuration_errors?: PaperworkConfigurationError[];
  items: PaperworkProposalItem[];
  additional_items?: PaperworkAdditionalItem[];
  available_topics?: PaperworkAvailableTopic[];
  prior_imports?: PaperworkPriorImport[];
}

export async function getNotePaperwork(noteId: string): Promise<{ paperwork: PaperworkProposal[] }> {
  const response = await typedFetchRaw(`/api/notes/${noteId}/paperwork`);
  if (!response.ok) throw new Error('Unable to load completed paperwork');
  return response.json() as Promise<{ paperwork: PaperworkProposal[] }>;
}

export async function importNotePaperwork(
  noteId: string,
  payload: {
    response_id: string;
    expected_updated_at?: string;
    idempotency_key: string;
    allow_reimport?: boolean;
  },
): Promise<Record<string, unknown>> {
  return typedPost<Record<string, unknown>, typeof payload>(
    `/api/notes/${noteId}/paperwork/import`,
    payload,
  );
}

export async function addNotePaperworkAdditional(
  noteId: string,
  payload: {
    response_id: string;
    source_field_path: string;
    note_topic_id?: string;
    new_topic_label?: string;
    expected_updated_at?: string;
    idempotency_key: string;
    allow_reimport?: boolean;
  },
): Promise<Record<string, unknown>> {
  return typedPost<Record<string, unknown>, typeof payload>(
    `/api/notes/${noteId}/paperwork/additional`,
    payload,
  );
}

export interface PaperworkReportTopic {
  topic_id: string;
  label: string;
  source?: 'template' | 'paperwork';
  template_field_id?: string | null;
  reports: Array<{
    answer: unknown;
    respondent?: { kind?: string; id?: string; display_name?: string } | null;
    described_person?: { kind?: string; id?: string; display_name?: string } | null;
    source_label?: string;
  }>;
  provider_entry?: string | null;
}

export async function updatePaperworkTopic(
  noteId: string,
  payload: { topic_id: string; provider_entry: string; expected_updated_at?: string },
): Promise<Record<string, unknown>> {
  return typedPut<Record<string, unknown>, typeof payload>(
    `/api/notes/${noteId}/paperwork/topics`,
    payload,
  );
}

/* -------------------------------------------------------------------------- */
/*  API functions                                                             */
/* -------------------------------------------------------------------------- */

export interface CreateNoteParams {
  patient_id: string;
  body: string;
}

/**
 * Create a new note.
 *
 * POST /api/notes → 201 NoteOut
 */
export async function createNote({ patient_id, body }: CreateNoteParams): Promise<NoteOut> {
  if (process.env.NODE_ENV !== 'production') {
    logger.log('[DEBUG] createNote payload', { patient_id, bodyPresent: !!(body && body.trim()) });
    if (!patient_id || !String(patient_id).trim() || !body || !String(body).trim()) {
      throw new Error('[DEBUG] Missing patient_id or body for createNote');
    }
  }
  return typedPost<NoteOut, CreateNoteParams>('/api/notes', { patient_id, body });
}

export interface InsertContentParams {
  note_id: string;
  content?: string;
  expected_updated_at?: string;
}

/**
 * Insert generated content into an existing note.
 *
 * POST /api/notes/insert-content
 */
export async function insertContent(
  noteId: string,
  content?: string,
  expectedUpdatedAt?: string,
): Promise<Record<string, unknown>> {
  const payload: InsertContentParams = { note_id: noteId };
  if (content) payload.content = content;
  if (expectedUpdatedAt) payload.expected_updated_at = expectedUpdatedAt;
  return typedPost<Record<string, unknown>, InsertContentParams>('/api/notes/insert-content', payload);
}

/**
 * Finalize an AI engine session and persist results.
 *
 * POST /api/engine/finalize-session
 */
export async function finalizeSession(
  sessionId: string,
): Promise<Record<string, unknown>> {
  return typedPost<Record<string, unknown>, { session_id: string }>(
    '/api/engine/finalize-session',
    { session_id: sessionId },
  );
}

export interface BulkExportResult {
  blob: Blob;
  exportedCount: number;
  skippedCount: number;
}

/**
 * Bulk-export multiple notes as a single PDF (or other format).
 *
 * POST /api/notes/bulk-export → binary blob + header counts
 */
export async function bulkExportNotes(
  noteIds: string[],
  format: string = 'pdf',
): Promise<BulkExportResult> {
  const res = await typedFetchRaw('/api/notes/bulk-export', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ note_ids: noteIds, format }),
  });
  if (!res.ok) {
    const errorText = await res.text();
    throw new ApiError(res.status, errorText || 'bulk-export failed', '/api/notes/bulk-export');
  }
  const exportedCount = parseInt(res.headers.get('X-Exported-Count') || '0', 10);
  const skippedCount = parseInt(res.headers.get('X-Skipped-Count') || '0', 10);
  const blob = await res.blob();
  return { blob, exportedCount, skippedCount };
}

/* -------------------------------------------------------------------------- */
/*  Encounter operations (from note page)                                     */
/* -------------------------------------------------------------------------- */

/** Fields editable on the encounter from the note page. */
export interface EncounterUpdateFromNote {
  scheduled_start?: string;
  scheduled_end?: string;
  practitioner_id?: string;
  appointment_type_id?: string;
  cpt_code_id?: string;
  cpt_codes?: Array<{ cpt_code_id: string; units?: number }>;
  location_id?: string;
  telehealth?: boolean;
  attendance_status?: string;
  billing_fee_override?: number | null;
  admin_fee?: number | null;
  admin_fee_description?: string | null;
  split_payment_enabled?: boolean;
  split_method?: string;
  split_payers?: Array<Record<string, unknown>>;
  attendees?: Array<{
    client_id?: string | null;
    contact_id?: string | null;
    display_name?: string | null;
    name?: string | null;
    role?: string | null;
    status?: string | null;
    required?: boolean;
  }>;
  /** Note-centered correction context (e.g. an active amendment) forwarded to
   * the canonical update pipeline. Authorization context, not a financial
   * bypass — all artifact guards still apply. */
  correction_reason?: string;
}

/** Request body for fee preview. */
export interface EncounterFeePreviewRequest {
  appointment_type_id: string;
  duration_minutes: number;
  primary_patient_id: string;
  case_id: string;
  provider_id?: string;
}

/** Fee preview response shape from the backend. */
export interface EncounterFeePreviewResponse {
  appointment_type_name: string | null;
  practitioner_name: string | null;
  current: {
    billing_fee_base: string | null;
    billing_fee_calculated: string | null;
    billing_fee_override: string | null;
    billing_fee_final: string | null;
    admin_fee: string | null;
    admin_fee_description: string | null;
    pricing_trace: Record<string, unknown>;
  };
  proposed: {
    billing_fee_base: string | null;
    billing_fee_calculated: string | null;
    source: string;
    requires_manual_fee: boolean;
    admin_fee: string | null;
    admin_fee_description: string | null;
    discount: {
      has_discount: boolean;
      discount_pct?: string;
      discount_amount?: string;
      source?: string;
    } | null;
    warning?: string;
  };
  split_payment: {
    split_method: string;
    old_total: string;
    new_total: string;
    old_shares: Array<{ label: string; amount: string }>;
    new_shares: Array<{ label: string; amount: string }>;
  } | null;
  payment_status: {
    has_payments: boolean;
    total_paid: string;
    patient_amount_paid: string;
    insurance_amount_paid: string;
  };
}

/**
 * Update encounter fields directly from the note page.
 *
 * PUT /api/notes/{noteId}/encounter
 */
/**
 * Open the No Show / Late Cancel note of a missed encounter, creating it once.
 *
 * POST /api/notes/missed-session
 */
export async function openMissedSessionNote(
  encounterId: string,
): Promise<{ note_id: string; created: boolean }> {
  return typedPost<{ note_id: string; created: boolean }, { encounter_id: string }>(
    '/api/notes/missed-session',
    { encounter_id: encounterId },
  );
}

export async function updateEncounterFromNote(
  noteId: string,
  data: EncounterUpdateFromNote,
): Promise<Record<string, unknown>> {
  return typedPut<Record<string, unknown>, EncounterUpdateFromNote>(
    `/api/notes/${noteId}/encounter`,
    data,
  );
}

/**
 * Preview fee changes when billing fields change on the note page.
 *
 * POST /api/notes/{noteId}/encounter/fee-preview
 */
export async function encounterFeePreview(
  noteId: string,
  data: EncounterFeePreviewRequest,
): Promise<EncounterFeePreviewResponse> {
  return typedPost<EncounterFeePreviewResponse, EncounterFeePreviewRequest>(
    `/api/notes/${noteId}/encounter/fee-preview`,
    data,
  );
}

/**
 * Clear the needs_payment_review flag after user dismisses the popup.
 *
 * PUT /api/notes/{noteId}/encounter/clear-payment-review
 */
export async function clearPaymentReview(
  noteId: string,
): Promise<Record<string, unknown>> {
  return typedPut<Record<string, unknown>, Record<string, never>>(
    `/api/notes/${noteId}/encounter/clear-payment-review`,
    {},
  );
}

export default { createNote, bulkExportNotes, insertContent, finalizeSession, updateEncounterFromNote, encounterFeePreview, clearPaymentReview };
